import { Icon, type IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

/*
 * Figma component set „Button Icon“ (Styleguide 28:375): square icon button.
 *   variant  primary | dark (Figma Secondary) | outline (white with shadow, border on hover; Figma
 *            Disabled = line border, tertiary icon, no shadow) | ghost
 *   size     lg 58 px (icon 24) | md 48 px (icon 20) | sm 38 px (icon 16)
 */
const VARIANTS = {
  primary: 'bg-[#127A26] text-white hover:bg-[#0B5719] active:bg-[#084512]',
  dark: 'bg-[#0D110D] text-white hover:bg-[#262C26] active:bg-[#363D36]',
  outline:
    'border border-transparent bg-white text-[#0D110D] shadow-float hover:border-[#CDD2CA] hover:bg-[#F5F6F3] hover:shadow-none active:bg-[#EBEDE8] focus-visible:border-[#127A26] disabled:border-[#E2E5DF] disabled:text-[#8F978E] disabled:opacity-100 disabled:shadow-none',
  ghost: 'text-[#0D110D] hover:bg-[#F5F6F3] active:bg-[#EBEDE8]',
} as const;

const SIZES = {
  lg: { box: 'size-[58px] rounded-2xl', icon: 24 },
  md: { box: 'size-12 rounded-2xl', icon: 20 },
  sm: { box: 'size-[38px] rounded-xl', icon: 16 },
} as const;

export type IconButtonVariant = keyof typeof VARIANTS;
export type IconButtonSize = keyof typeof SIZES;

type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: IconName;
  /** Accessible name – the button has no visible text. */
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Render the child element (a link) with the button look. */
  asChild?: boolean;
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { icon, label, variant = 'outline', size = 'md', asChild, className, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const content = <Icon name={icon} size={SIZES[size].icon} />;
    return (
      <Comp
        ref={ref}
        aria-label={label}
        {...(asChild ? {} : { type: 'button' as const })}
        {...props}
        className={cn(
          'inline-flex shrink-0 items-center justify-center transition-colors',
          'focus-visible:shadow-focus focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40',
          VARIANTS[variant],
          SIZES[size].box,
          className,
        )}
      >
        {asChild && React.isValidElement<{ children?: React.ReactNode }>(children)
          ? React.cloneElement(children, { children: content })
          : content}
      </Comp>
    );
  },
);
IconButton.displayName = 'IconButton';

/** The icon-button look for a non-interactive box inside a link (cards whose whole surface is the link). */
export function IconBox({
  icon,
  variant = 'outline',
  size = 'md',
  className,
}: {
  icon: IconName;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center transition-colors',
        VARIANTS[variant],
        SIZES[size].box,
        className,
      )}
    >
      <Icon name={icon} size={SIZES[size].icon} />
    </span>
  );
}
